import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import { relireApres } from '../partage/relectures';
import { ceQuEmporte, decalageAuLacher, peutSeGlisser, type Point } from './deplacerUneBulle';

/** En pixels d'écran : sous ce mouvement, c'est un clic (la fiche, la flèche) ; le seuil du fond et des papiers. */
const SEUIL_DU_GLISSER_PX = 5;

/** Une bulle qu'on glisse, ou qu'on vient de lâcher et qui attend la carte du serveur. */
export interface BulleGlissee {
  id: string;
  /** Elle et ce qu'elle porte à l'écran : tout suit la main. */
  emportees: ReadonlySet<string>;
  /** L'écart de la main depuis l'appui, sur la carte. */
  ecart: Point;
  /** La main tient encore la bulle ; faux, elle est lâchée et attend la réponse. */
  enMain: boolean;
}

interface Tir {
  id: string;
  pointeur: number;
  appui: Point;
  appuiSurLaCarte: Point;
  /** Les places du serveur à l'appui : le décalage se compte depuis elles. */
  places: Readonly<Record<string, Point>>;
  parti: boolean;
}

/** Ce qui garde son clic : l'état, la flèche, le « ⋯ » (qui arrête déjà l'appui lui-même). */
const GARDENT_LEUR_CLIC = '.noeud-pastille, .noeud-chevron, .menu-habit';

/**
 * Glisser une bulle sur la carte, cadenas ouvert (chantier deplacer-les-bulles, ticket 03, ADR
 * 0016), sur le modèle du glisser d'un papier (`papiers/useGlisserUnPapier.ts`).
 *
 * Un appui sur une bulle (pas le centre, ni son état, sa flèche ou son « ⋯ ») ne glisse qu'au-delà
 * du seuil : en deçà, c'est un clic, qui garde son effet. Pendant le geste, la bulle et ce qu'elle
 * porte suivent la main, au zoom du moment, et le regard ne bouge pas. **Au lâcher, une fois**, son
 * décalage par rapport au centre de sa mère part au serveur pour la profondeur affichée, puis la
 * carte se relit ; d'ici là, la bulle reste où on l'a lâchée. Le client ne place rien (ADR 0008) :
 * c'est le serveur qui range le reste autour.
 *
 * `commencer` rend vrai quand l'appui est pour la bulle : la carte ne le donne pas à la caméra.
 */
export function useGlisserUneBulle({
  cadenasOuvert,
  enDesignation,
  enColonnes,
  profondeur,
  centreId,
  places,
  parId,
  enfantsDe,
  versLaCarte,
}: {
  cadenasOuvert: boolean;
  /** Lier ou Hiérarchiser en cours : aucune bulle ne se glisse. */
  enDesignation: boolean;
  /** Regrouper : les colonnes ne se glissent pas. */
  enColonnes: boolean;
  profondeur: number;
  centreId: string;
  /** Les places du serveur, celles qu'on voit sans glisser. */
  places: Readonly<Record<string, Point>>;
  parId: ReadonlyMap<string, { parentId: string | null }>;
  enfantsDe: ReadonlyMap<string, readonly string[]>;
  versLaCarte: (clientX: number, clientY: number) => Point | null;
}) {
  const client = useQueryClient();
  const [glissee, setGlissee] = useState<BulleGlissee | null>(null);
  /** La même, lue au lâcher sans attendre que React l'ait dessinée. */
  const montree = useRef<BulleGlissee | null>(null);
  const montrer = useCallback((bulle: BulleGlissee | null) => {
    montree.current = bulle;
    setGlissee(bulle);
  }, []);
  const tir = useRef<Tir | null>(null);
  const actuel = useRef({ profondeur, centreId, parId, enfantsDe, versLaCarte });
  actuel.current = { profondeur, centreId, parId, enfantsDe, versLaCarte };

  const commencer = useCallback(
    (evenement: React.PointerEvent): boolean => {
      if (evenement.button !== 0 || !evenement.isPrimary) return false;
      const cible = evenement.target as HTMLElement;
      const noeud = cible.closest<HTMLElement>('.noeud');
      const id = noeud?.dataset.bulle;
      if (!id || cible.closest(GARDENT_LEUR_CLIC)) return false;
      // Une bulle lâchée attend encore sa place : on attend qu'elle l'ait, le fond se glisse en attendant.
      if (montree.current) return false;
      const situation = {
        cadenasOuvert,
        enDesignation,
        enColonnes,
        estLeCentre: id === centreId,
        cachee: !(id in places),
      };
      if (!peutSeGlisser(situation)) return false;
      const surLaCarte = versLaCarte(evenement.clientX, evenement.clientY);
      if (!surLaCarte) return false;
      tir.current = {
        id,
        pointeur: evenement.pointerId,
        appui: { x: evenement.clientX, y: evenement.clientY },
        appuiSurLaCarte: surLaCarte,
        places,
        parti: false,
      };
      return true;
    },
    [cadenasOuvert, enDesignation, enColonnes, centreId, places, versLaCarte]
  );

  useEffect(() => {
    const bouger = (evenement: PointerEvent) => {
      const t = tir.current;
      if (!t || evenement.pointerId !== t.pointeur) return;
      if (evenement.buttons === 0) {
        lever(evenement);
        return;
      }
      if (!t.parti) {
        const bouge = Math.hypot(evenement.clientX - t.appui.x, evenement.clientY - t.appui.y);
        if (bouge < SEUIL_DU_GLISSER_PX) return;
        t.parti = true;
        window.getSelection()?.removeAllRanges();
      }
      evenement.preventDefault();
      const ici = actuel.current.versLaCarte(evenement.clientX, evenement.clientY);
      if (!ici) return;
      const ecart = { x: ici.x - t.appuiSurLaCarte.x, y: ici.y - t.appuiSurLaCarte.y };
      const avant = montree.current;
      montrer({
        id: t.id,
        emportees: avant?.id === t.id ? avant.emportees : ceQuEmporte(t.id, actuel.current.enfantsDe, t.places),
        ecart,
        enMain: true,
      });
    };
    const lever = (evenement: PointerEvent) => {
      const t = tir.current;
      if (!t || evenement.pointerId !== t.pointeur) return;
      tir.current = null;
      if (!t.parti) return;
      // Le clic qui suit un glisser n'ouvre rien : ni la fiche de la bulle, ni ce qui est dessous.
      const avaler = (clic: MouseEvent) => {
        clic.stopPropagation();
        clic.preventDefault();
      };
      window.addEventListener('click', avaler, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', avaler, { capture: true }), 0);
      const lachee = montree.current;
      if (!lachee || lachee.id !== t.id) {
        montrer(null);
        return;
      }
      montrer({ ...lachee, enMain: false });
      const { profondeur: ici, parId: meres, centreId: centre } = actuel.current;
      const decalage = decalageAuLacher(t.id, t.places, meres, centre, lachee.ecart);
      // Posée ou refusée, on relit la carte : elle dit la place gardée, ou celle d'avant.
      void api
        .placerSurLaCarte(t.id, ici, decalage)
        .catch(() => undefined)
        .then(() => relireApres(client, 'placerSurLaCarte'))
        .finally(() => montrer(null));
    };
    window.addEventListener('pointermove', bouger);
    window.addEventListener('pointerup', lever);
    window.addEventListener('pointercancel', lever);
    return () => {
      window.removeEventListener('pointermove', bouger);
      window.removeEventListener('pointerup', lever);
      window.removeEventListener('pointercancel', lever);
    };
  }, [client, montrer]);

  return { glissee, commencer };
}
